import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../services/api';
import { ApiError } from '../services/types';
import { useAppStore } from '../store/appStore';

/**
 * Fetch the four artifacts for a run detail page.
 *
 * Fetch-guard strategy
 * --------------------
 * We need guards against duplicate fetches in three scenarios:
 *   1. StrictMode: React mount → unmount → remount in dev. Local `loading`
 *      state is wiped on each remount, so we use a module-scoped `Set` to
 *      track in-flight fetch keys that survive React's lifecycle.
 *   2. Cached: artifact already in Zustand (tab switch). Check `run.*` first.
 *   3. Error: previous fetch failed. Error stored in Zustand (not local state)
 *      so it also survives StrictMode remounts. A "Retry" button clears it.
 */

type ArtifactKind = 'fia' | 'schema' | 'manifest' | 'hits';

/** Keys of in-flight fetches that should not be duplicated. */
const inFlight = new Set<string>();

export interface UseRunReturn {
  runId: string;
  run: ReturnType<typeof useAppStore.getState>['runs'][string];
  loading: Record<ArtifactKind, boolean>;
  errors: Partial<Record<ArtifactKind, string>>;
  /** HTTP status per artifact error (when the error was an ApiError). */
  errorStatuses: Partial<Record<ArtifactKind, number>>;
  fetchFia: () => Promise<void>;
  fetchSchema: () => Promise<void>;
  fetchManifest: () => Promise<void>;
  fetchHits: () => Promise<void>;
  retryFia: () => Promise<void>;
  fetchAll: () => Promise<void>;
}

export function useRun(runId: string): UseRunReturn {
  const run = useAppStore((s) => s.runs[runId] ?? { runId });
  const updateRun = useAppStore((s) => s.updateRun);
  const replaceHits = useAppStore((s) => s.replaceHits);

  // Loading is local — it's fine to reset on remount since in-flight
  // tracking is handled by the `inFlight` Set.
  const [loading, setLoading] = useState<Record<ArtifactKind, boolean>>({
    fia: false,
    schema: false,
    manifest: false,
    hits: false,
  });

  // Errors live in the Zustand store so they survive StrictMode remounts
  // and prevent the auto-fetch from re-firing after a failure.
  const fetchErrors = run.fetchErrors ?? {};
  const fetchErrorStatuses = run.fetchErrorStatuses ?? {};

  const wrap = useCallback(
    async (kind: ArtifactKind, fn: () => Promise<void>) => {
      const key = `${runId}:${kind}`;
      if (inFlight.has(key)) return; // deduplicate concurrent calls
      inFlight.add(key);

      setLoading((l) => ({ ...l, [kind]: true }));
      // Clear any previous error for this artifact before retrying.
      const prior = useAppStore.getState().runs[runId];
      updateRun(runId, {
        fetchErrors: { ...(prior?.fetchErrors ?? {}), [kind]: undefined },
        fetchErrorStatuses: { ...(prior?.fetchErrorStatuses ?? {}), [kind]: undefined },
      });
      try {
        await fn();
      } catch (err) {
        // Surface the backend's message directly — after api.ts unwraps
        // FastAPI's envelope this is already the human-readable string.
        // We no longer prefix with `API ${status}:` because callers can
        // read the status from `errorStatuses` when they need to branch.
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Unknown error';
        const status = err instanceof ApiError ? err.status : undefined;
        const current = useAppStore.getState().runs[runId];
        updateRun(runId, {
          fetchErrors: { ...(current?.fetchErrors ?? {}), [kind]: message },
          fetchErrorStatuses: { ...(current?.fetchErrorStatuses ?? {}), [kind]: status },
        });
      } finally {
        inFlight.delete(key);
        setLoading((l) => ({ ...l, [kind]: false }));
      }
    },
    [runId, updateRun],
  );

  const fetchFia = useCallback(
    () =>
      wrap('fia', async () => {
        const fia = await api.getFiaArtifact(runId);
        updateRun(runId, { fia });
      }),
    [runId, updateRun, wrap],
  );

  const fetchSchema = useCallback(
    () =>
      wrap('schema', async () => {
        const schema = await api.getSchema(runId);
        updateRun(runId, { schema });
      }),
    [runId, updateRun, wrap],
  );

  const fetchManifest = useCallback(
    () =>
      wrap('manifest', async () => {
        const manifest = await api.getManifest(runId);
        updateRun(runId, { manifest });
      }),
    [runId, updateRun, wrap],
  );

  const fetchHits = useCallback(
    () =>
      wrap('hits', async () => {
        const hits = await api.listHits(runId);
        updateRun(runId, { hits });
        // v0.6 Stage 1 (H3) — keep the cross-run hitsIndex consistent
        // with the freshly-fetched run-scoped list. Replacing (not
        // upserting) drops resolved hits that lingered from earlier
        // pre-H1 fetches.
        replaceHits(runId, hits);
      }),
    [runId, replaceHits, updateRun, wrap],
  );

  // Clear the Zustand error so the guard lets the next call through.
  const retryFia = useCallback(() => {
    const current = useAppStore.getState().runs[runId];
    updateRun(runId, {
      fetchErrors: { ...(current?.fetchErrors ?? {}), fia: undefined },
      fetchErrorStatuses: { ...(current?.fetchErrorStatuses ?? {}), fia: undefined },
    });
    return fetchFia();
  }, [runId, updateRun, fetchFia]);

  const fetchAll = useCallback(
    () => Promise.all([fetchFia(), fetchSchema(), fetchManifest(), fetchHits()]).then(() => undefined),
    [fetchFia, fetchSchema, fetchManifest, fetchHits],
  );

  // Auto-fetch all four artifacts on mount.
  //
  // We used to lazy-fetch schema/manifest/hits on first tab open, but the
  // mount-only `useEffect` inside TabPanel doesn't fire reliably through
  // React Router's nested-Routes transitions (the element is preserved
  // across URL changes so no remount happens). Simpler and more robust to
  // fetch everything upfront — the mock returns in milliseconds and the
  // `inFlight` Set already prevents duplicate fires from StrictMode.
  //
  // Each fetch is independent: a failure on one artifact (e.g. manifest
  // not yet written) still lets the others succeed.
  const autoFetchedRef = useRef(false);
  useEffect(() => {
    if (autoFetchedRef.current) return;
    autoFetchedRef.current = true;
    if (!run.fia && !fetchErrors.fia) void fetchFia();
    if (!run.schema && !fetchErrors.schema) void fetchSchema();
    if (!run.manifest && !fetchErrors.manifest) void fetchManifest();
    if (!run.hits && !fetchErrors.hits) void fetchHits();
    // Deliberately empty deps — fetch exactly once on mount. Retries go
    // through the Retry button, and the `inFlight` Set handles StrictMode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch artifacts when the run's status transitions.
  //
  // Without this, the mount-only auto-fetch above caches its result for
  // the page's lifetime. That's fine for a terminal run — but if the
  // pipeline is awaiting_review, the reviewer resolves a hit, and the
  // pipeline auto-resumes (running → complete), the frontend never
  // re-fetches the manifest it previously got a 404 on. The MOA tab
  // stays stuck on "not yet generated" even though the plan is now
  // available server-side.
  //
  // Strategy: clear the per-artifact errors on every status change so
  // the next fetch is a clean retry, then re-issue the fetches. We do
  // this for the artifacts that can plausibly change across a status
  // transition: schema (might have been unavailable), manifest (plan
  // lands when MOA runs), hits (new HITL or resolved ones). FIA is
  // deterministic per run, so we skip it to save a round-trip.
  const prevStatusRef = useRef<typeof run.status>(undefined);
  useEffect(() => {
    const prev = prevStatusRef.current;
    const current = run.status;
    prevStatusRef.current = current;
    // Skip first render — the auto-fetch above handles the initial load.
    if (prev === undefined || prev === current) return;

    // Clear cached errors so useRun.wrap() will actually fire fetches
    // that were previously failed.
    const snapshot = useAppStore.getState().runs[runId];
    updateRun(runId, {
      fetchErrors: {
        ...(snapshot?.fetchErrors ?? {}),
        schema: undefined,
        manifest: undefined,
        hits: undefined,
      },
      fetchErrorStatuses: {
        ...(snapshot?.fetchErrorStatuses ?? {}),
        schema: undefined,
        manifest: undefined,
        hits: undefined,
      },
    });

    void fetchSchema();
    void fetchManifest();
    void fetchHits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.status]);

  return {
    runId,
    run,
    loading,
    errors: fetchErrors,
    errorStatuses: fetchErrorStatuses,
    fetchFia,
    fetchSchema,
    fetchManifest,
    fetchHits,
    retryFia,
    fetchAll,
  };
}
